import { regulatorySourceMaintenanceText } from './sources';
/**
 * Frozen regulatory feasibility screen on new reports (R5).
 *
 * Lifecycle (campus-resource-advisory pattern):
 *   1. `freezeRegulatoryFeasibilityForReport` runs once before model dispatch
 *      for the frozen report types (feasibility only) and never in AI-only
 *      evidence mode. It derives the input (R2), runs the pure screen (R1) and
 *      binds both to the site, the consumed SED and the persisted intent.
 *   2. The report stores the record as `regulatoryFeasibilityScreenAtGen`.
 *   3. `readRegulatoryFeasibilityScreen` validates only; it never recomputes
 *      the screen or reads live evidence. A STORED record is parsed by a
 *      parser keyed by its screen version (structure only) and checked
 *      against its input, result and frozen hashes; a tampered result or hash
 *      throws. The doctrine contract and live copy are enforced when a result
 *      is produced, never re-judged on read, so a later copy edit or version
 *      bump leaves every stored report readable.
 *   4. `assertRegulatoryFeasibilityScreenAtCommit` is the finalizer fence: the
 *      SED identity must equal the consumed snapshot and the persisted intent
 *      must still hash to the bound value.
 *   5. The immutable document carries ONE appendix structured-data block
 *      whose single non-array value holds every row and the qualifiers, so a
 *      selection surface (Report Summary) cannot pick a subset. The block holds
 *      the result only: the input (which can include uploaded zoning text)
 *      never leaves the private frozen record.
 *
 * No SED key is added, so SED material-dependency manifests and section
 * fingerprints are unchanged; legacy reports build byte-identical documents.
 */
import { z } from 'zod';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { ReportDocumentBlockV1, ReportDocumentModelV1 } from '../../types/reportContractsV2';
import type { CoverageManifestAtGen } from '../coverage/evidenceConsolidation';
import { canonicalJson, hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { hashProjectIntent } from '../sed/shouldBootstrap';
import { regulatoryFeasibilityInputs } from './inputs';
import { screenRegulatoryFeasibility } from './screen';
import {
  CONNECTION_SCENARIO_LABEL, REGULATORY_FEASIBILITY_SCREEN_VERSION, REGULATORY_ISSUE_LABEL, REGULATORY_ISSUE_STATUS_LABEL,
  SOURCES_CHECKED_ON, STORED_REGULATORY_SCREEN_RESULT_PARSERS, STORED_REGULATORY_SCREEN_VERSIONS, UNRESOLVED_REASON_LABEL,
  regulatoryFeasibilityInputSchema, regulatoryFeasibilityScreenResultSchema,
  type RegulatoryFeasibilityScreenResult, type StoredRegulatoryScreenVersion,
} from './types';

/** Report types that freeze the screen (owner decision: feasibility only). */
export const REGULATORY_FEASIBILITY_FROZEN_REPORT_TYPES: ReadonlySet<string> = new Set(['feasibility']);

const sha = z.string().regex(/^[a-f0-9]{64}$/);
const nonEmpty = z.string().min(1);
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const projectionSchema = z.object({
  schemaVersion: z.literal(1),
  input: regulatoryFeasibilityInputSchema,
  inputHash: sha,
  result: regulatoryFeasibilityScreenResultSchema,
  resultHash: sha,
}).strict().superRefine((value, context) => {
  if (value.inputHash !== hashCanonicalJson(value.input) || value.resultHash !== hashCanonicalJson(value.result)) {
    context.addIssue({ code: 'custom', message: 'Frozen regulatory screen input/result identity mismatch.' });
  }
});

const bindingSchema = z.object({
  siteId: nonEmpty,
  sedId: nonEmpty,
  sedVersion: z.number().int().nonnegative(),
  /** `regulatoryIntentHash` of the site's persisted Project Intent. */
  intentHash: nonEmpty,
  documentInputHash: nonEmpty.nullable(),
  screenVersion: z.literal(REGULATORY_FEASIBILITY_SCREEN_VERSION),
  sourcesCheckedOn: z.literal(SOURCES_CHECKED_ON),
}).strict();

const frozenSchema = z.object({
  schemaVersion: z.literal(1),
  binding: bindingSchema,
  projection: projectionSchema,
  frozenHash: sha,
}).strict().superRefine((value, context) => {
  if (value.frozenHash !== hashCanonicalJson({ binding: value.binding, projection: value.projection })) {
    context.addIssue({ code: 'custom', message: 'Frozen regulatory screen authority identity mismatch.' });
  }
});
export type FrozenRegulatoryFeasibilityScreen = z.infer<typeof frozenSchema>;

// ── Stored records: version-keyed structure plus identity ───────────────────

const storedVersion = z.enum(STORED_REGULATORY_SCREEN_VERSIONS);
/** Only the screen version is read before the version-keyed parser runs. */
const storedResultEnvelope = z.object({ screenVersion: storedVersion }).passthrough();

const storedFrozenSchema = z.object({
  schemaVersion: z.literal(1),
  binding: z.object({
    siteId: nonEmpty,
    sedId: nonEmpty,
    sedVersion: z.number().int().nonnegative(),
    intentHash: nonEmpty,
    documentInputHash: nonEmpty.nullable(),
    screenVersion: storedVersion,
    sourcesCheckedOn: calendarDate,
  }).strict(),
  projection: z.object({
    schemaVersion: z.literal(1),
    // The private input is bound by its hash only; its shape may evolve.
    input: z.object({ schemaVersion: z.literal(1) }).passthrough(),
    inputHash: sha,
    result: storedResultEnvelope,
    resultHash: sha,
  }).strict(),
  frozenHash: sha,
}).strict();

/** Parse a stored result with the parser of its own screen version. */
function parseStoredResult(value: unknown, version: StoredRegulatoryScreenVersion): RegulatoryFeasibilityScreenResult {
  const result = STORED_REGULATORY_SCREEN_RESULT_PARSERS[version].parse(value);
  if (result.screenVersion !== version) throw new Error('Frozen regulatory screen version mismatch.');
  return result;
}

/** A stored frozen record: structure by its screen version, then the input,
 * result and frozen hashes over the stored values exactly. */
function parseStoredFrozen(value: unknown) {
  const frozen = storedFrozenSchema.parse(value);
  const { binding, projection } = frozen;
  const result = parseStoredResult(projection.result, binding.screenVersion);
  if (projection.inputHash !== hashCanonicalJson(projection.input) || projection.resultHash !== hashCanonicalJson(projection.result)) {
    throw new Error('Frozen regulatory screen input/result identity mismatch.');
  }
  if (frozen.frozenHash !== hashCanonicalJson({ binding, projection })) {
    throw new Error('Frozen regulatory screen authority identity mismatch.');
  }
  return { binding, result, resultHash: projection.resultHash };
}

/** Public-safe document carrier: the result and its identity, never the input. */
export interface RegulatoryFeasibilityDocumentCarrier {
  schemaVersion: 1;
  screenVersion: StoredRegulatoryScreenVersion;
  sourcesCheckedOn: string;
  resultHash: string;
  result: RegulatoryFeasibilityScreenResult;
}

const storedCarrierSchema = z.object({
  schemaVersion: z.literal(1),
  screenVersion: storedVersion,
  sourcesCheckedOn: calendarDate,
  resultHash: sha,
  result: storedResultEnvelope,
}).strict();

/** A stored carrier: structure by its screen version plus its result hash. */
function parseStoredCarrier(value: unknown): RegulatoryFeasibilityDocumentCarrier {
  const carrier = storedCarrierSchema.parse(value);
  const result = parseStoredResult(carrier.result, carrier.screenVersion);
  if (carrier.resultHash !== hashCanonicalJson(carrier.result)) {
    throw new Error('Regulatory screen document carrier identity mismatch.');
  }
  return { ...carrier, result };
}

/** Canonical caller context; the intent is the site's PERSISTED intent, not
 * a one-shot report override, so the commit fence can compare like for like. */
export interface RegulatoryFeasibilityAuthority {
  siteId: string;
  siteProjectIntent: ProjectIntent | null | undefined;
}

type ScreenReport = Pick<SiteRagReport, 'siteId' | 'regulatoryFeasibilityScreenAtGen'>;
type BoundSed = Pick<SiteEvidenceDocument, 'id' | 'version' | 'documentInputHash'>;

/**
 * Intent identity used by the binding: `hashProjectIntent` over the intent's
 * JSON form, so an in-memory `undefined` key hashes like the persisted
 * document that drops it.
 */
export function regulatoryIntentHash(intent: ProjectIntent | null | undefined): string {
  return hashProjectIntent(intent == null ? null : JSON.parse(canonicalJson(intent)) as ProjectIntent);
}

function bindingFor(sed: BoundSed, authority: RegulatoryFeasibilityAuthority) {
  return bindingSchema.parse({
    siteId: authority.siteId,
    sedId: sed.id,
    sedVersion: sed.version,
    intentHash: regulatoryIntentHash(authority.siteProjectIntent),
    documentInputHash: sed.documentInputHash ?? null,
    screenVersion: REGULATORY_FEASIBILITY_SCREEN_VERSION,
    sourcesCheckedOn: SOURCES_CHECKED_ON,
  });
}

/** Freeze the screen from the consumed intent, SED and coverage manifest. */
export function freezeRegulatoryFeasibilityScreen(args: {
  sed: SiteEvidenceDocument;
  intent: ProjectIntent | null | undefined;
  coverageManifest?: Pick<CoverageManifestAtGen, 'entries'> | null;
  authority: RegulatoryFeasibilityAuthority;
}): FrozenRegulatoryFeasibilityScreen {
  const input = regulatoryFeasibilityInputs({ intent: args.intent, sed: args.sed, coverageManifest: args.coverageManifest });
  const result = screenRegulatoryFeasibility(input);
  const projection = projectionSchema.parse({ schemaVersion: 1, input, inputHash: hashCanonicalJson(input), result, resultHash: hashCanonicalJson(result) });
  const binding = bindingFor(args.sed, args.authority);
  return frozenSchema.parse({ schemaVersion: 1, binding, projection, frozenHash: hashCanonicalJson({ binding, projection }) });
}

/**
 * Generation seam: freeze only for the frozen report types, never in AI-only
 * evidence mode and never without an authority (legacy callers).
 */
export function freezeRegulatoryFeasibilityForReport(args: {
  reportType: string;
  aiOnlyEvidence: boolean;
  sed: SiteEvidenceDocument;
  intent: ProjectIntent | null | undefined;
  coverageManifest?: Pick<CoverageManifestAtGen, 'entries'> | null;
  authority: RegulatoryFeasibilityAuthority | undefined;
}): FrozenRegulatoryFeasibilityScreen | undefined {
  if (!REGULATORY_FEASIBILITY_FROZEN_REPORT_TYPES.has(args.reportType) || args.aiOnlyEvidence || !args.authority) return undefined;
  return freezeRegulatoryFeasibilityScreen({ sed: args.sed, intent: args.intent, coverageManifest: args.coverageManifest, authority: args.authority });
}

/** Read-time validation only. Throws on a tampered or foreign record. */
export function readRegulatoryFeasibilityScreen(report: ScreenReport): RegulatoryFeasibilityDocumentCarrier | undefined {
  const value = report.regulatoryFeasibilityScreenAtGen;
  if (value == null) return undefined;
  const frozen = parseStoredFrozen(value);
  if (report.siteId != null && frozen.binding.siteId !== report.siteId) {
    throw new Error('Frozen regulatory screen does not belong to this report. Regenerate this report.');
  }
  return parseStoredCarrier({
    schemaVersion: 1,
    screenVersion: frozen.binding.screenVersion,
    sourcesCheckedOn: frozen.binding.sourcesCheckedOn,
    resultHash: frozen.resultHash,
    result: frozen.result,
  });
}

/**
 * Commit fence. The record must be valid, the consumed SED snapshot must be
 * the bound SED (id, version, document input), and the persisted intent must
 * still hash to the bound value. Throws when anything moved.
 */
export function assertRegulatoryFeasibilityScreenAtCommit(report: ScreenReport, sed: BoundSed | null | undefined, live: {
  siteId: string; projectIntent: ProjectIntent | null | undefined;
}): void {
  if (report.regulatoryFeasibilityScreenAtGen == null) return;
  const { binding } = parseStoredFrozen(report.regulatoryFeasibilityScreenAtGen);
  if (binding.siteId !== live.siteId) throw new Error('Regulatory screen belongs to another site.');
  if (!sed || binding.sedId !== sed.id || binding.sedVersion !== sed.version || binding.documentInputHash !== (sed.documentInputHash ?? null)) {
    throw new Error('Regulatory screen SED inputs changed before persistence. Generate again.');
  }
  if (binding.intentHash !== regulatoryIntentHash(live.projectIntent)) {
    throw new Error('Project Intent changed during report generation; the regulatory screen is no longer current. Generate again.');
  }
}

export const REGULATORY_FEASIBILITY_PROMPT_HEADING = '[REGULATORY FEASIBILITY SCREEN — FROZEN FOR THIS REPORT; EVIDENCE-QUALIFIED, NOT A PERMIT, APPROVAL OR LEGAL CLEARANCE]';

/**
 * Deterministic prompt block for the frozen screen (R12). Empty when no screen
 * is frozen (legacy reports, other report types, AI-only mode), so those
 * prompts stay byte-identical. Only the validated result is printed — never
 * the private input. The model must reproduce each status verbatim.
 */
export function regulatoryFeasibilityScreenPrompt(frozen: FrozenRegulatoryFeasibilityScreen | undefined): string {
  if (!frozen) return '';
  const { result } = frozenSchema.parse(frozen).projection;
  const rows = result.issues.map(row => {
    const scenarios = row.affectedScenarios.map(scenario => CONNECTION_SCENARIO_LABEL[scenario]).join('; ');
    const status = row.status === 'unresolved' && row.unresolvedReason
      ? `${REGULATORY_ISSUE_STATUS_LABEL.unresolved} (${UNRESOLVED_REASON_LABEL[row.unresolvedReason]})`
      : REGULATORY_ISSUE_STATUS_LABEL[row.status];
    const authority = `${row.authority.level}${row.authority.name ? `: ${row.authority.name}` : ''}`;
    const sources = row.sources.length
      ? row.sources.map(source => `${source.label} (${source.asOf ?? 'undated'}; rule status ${source.ruleStatus})`).join('; ')
      : 'none recorded';
    return `- ${REGULATORY_ISSUE_LABEL[row.issue]} [${scenarios}]: ${status}${row.qualifier ? ` — ${row.qualifier}` : ''} Authority: ${authority}. Sources: ${sources}. ${row.implication} Next action: ${row.nextAction}`;
  });
  return `\n${REGULATORY_FEASIBILITY_PROMPT_HEADING}\n`
    + `${regulatorySourceMaintenanceText()} `
    + 'Use these statuses verbatim for these issues. Never upgrade one: "unresolved" never becomes "no blocker identified", and "approval required" never becomes approved, exempt or cleared. '
    + 'A narrative claim that contradicts a status here must be corrected to the status.\n'
    + `${rows.join('\n')}\n${result.qualifier}\n`;
}

export const REGULATORY_FEASIBILITY_BLOCK_ID = 'regulatory-feasibility-screen-v1';
export const REGULATORY_FEASIBILITY_BLOCK_LABEL = 'Regulatory feasibility screen (evidence-qualified; not a permit, approval or legal clearance)';

/** Exactly one appendix block; the value is one object, never an array. */
export function buildRegulatoryFeasibilityDocumentBlocks(value: unknown): ReportDocumentBlockV1[] {
  if (value == null) return [];
  const carrier = parseStoredCarrier(value);
  return [{
    id: REGULATORY_FEASIBILITY_BLOCK_ID,
    kind: 'structured-data',
    label: REGULATORY_FEASIBILITY_BLOCK_LABEL,
    value: JSON.parse(canonicalJson(carrier)) as Extract<ReportDocumentBlockV1, { kind: 'structured-data' }>['value'],
    sourceIds: [],
    citationIds: [],
  }];
}

/** Read the carrier back from an immutable document; validates placement. */
export function readRegulatoryFeasibilityFromDocument(document: ReportDocumentModelV1): RegulatoryFeasibilityDocumentCarrier | undefined {
  const owned = document.sections.flatMap(section => section.blocks
    .filter(block => block.id === REGULATORY_FEASIBILITY_BLOCK_ID)
    .map(block => ({ section: section.sectionId, block })));
  if (owned.length === 0) return undefined;
  const carrier = owned[0]!;
  if (owned.length !== 1 || carrier.section !== 'appendix' || carrier.block.kind !== 'structured-data') {
    throw new Error('Frozen regulatory screen has an invalid appendix contract.');
  }
  const parsed = parseStoredCarrier(carrier.block.value);
  if (canonicalJson(buildRegulatoryFeasibilityDocumentBlocks(parsed)[0]) !== canonicalJson(carrier.block)) {
    throw new Error('Frozen regulatory screen block disagrees with its carrier.');
  }
  return parsed;
}
